// src/pages/SmartAlerts.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaBell,
  FaBolt,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronRight,
  FaCity,
  FaEnvelope,
  FaGift,
  FaHotel,
  FaMapMarkedAlt,
  FaPaperPlane,
  FaPlane,
  FaRegClock,
  FaSearchLocation,
  FaShieldAlt,
  FaStar,
  FaUtensils,
  FaUsers,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../supabaseClient';
import { createSmartAlert, type SmartAlertPayload, type SmartAlertType } from '../lib/smartAlerts';
import { buildKiwiFlightSearchUrl } from '../lib/kiwiAffiliate';

const copy = {
  fr: {
    badge: 'Alertes intelligentes Forfeo',
    heroTitleA: 'Surveille ce que tu veux vivre,',
    heroTitleB: 'Forfeo te prévient.',
    subtitle: "Crée une alerte personnalisée pour les offres Last Call, restaurants, hôtels, idées cadeaux, régions du Québec et alertes billets d’avion. Forfeo analyse ton intention et t’avise lorsqu’une occasion pertinente apparaît.",
    create: 'Créer mon alerte',
    manage: 'Gérer mes alertes',
    formTitle: 'Créer une alerte intelligente',
    formSubtitle: 'Choisis ton intention, ton budget et ta région. Tu recevras seulement les alertes pertinentes.',
    email: 'Courriel de réception',
    name: 'Prénom ou nom',
    type: 'Type d’alerte',
    flightOrigin: 'Aéroport de départ',
    flightDestination: 'Aéroport de destination',
    travelPeriod: 'Période de voyage',
    passengers: 'Volsurs',
    directOnly: 'Vol direct seulement',
    flightBlockTitle: 'Créer mon alerte de vol',
    flightBlockText: 'Indique ton aéroport de départ, ta destination et ton budget. Forfeo t’envoie un courriel de confirmation et surveille les occasions de vols.',
    flightOriginPlaceholder: 'Ex: YQB, YUL, Québec, Montréal',
    flightDestinationPlaceholder: 'Ex: Paris, MIA, YVR, Fort Lauderdale',
    flightPeriodPlaceholder: 'Ex: Mars 2026, relâche, flexible',
    airportSuggestions: 'Suggestions populaires',
    originSuggestions: 'Départs fréquents',
    destinationSuggestions: 'Destinations populaires',
    prefilledFlightLink: 'Voir le lien prérempli',
    googleFlightsHint: 'Le bouton ouvre Google Flights avec ton départ, ta destination et ta période déjà inclus dans la recherche.',
    errorFlightRoute: 'Entre au minimum un départ et une destination pour créer une alerte de vol.',
    region: 'Région',
    city: 'Ville ou quartier',
    category: 'Catégorie',
    budget: 'Budget maximum',
    occasion: 'Occasion',
    keywords: 'Mots-clés / intention',
    frequency: 'Fréquence',
    consent: 'J’accepte de recevoir des alertes courriel Forfeo liées à mes critères. Je pourrai les gérer ou les désactiver à tout moment.',
    submit: 'Activer mon alerte',
    sending: 'Activation...',
    successTitle: 'Alerte activée',
    successText: 'Ton radar Forfeo est en place. Tu recevras un courriel lorsque des offres ou expériences correspondent à tes critères.',
    errorConsent: 'Tu dois accepter les alertes courriel pour activer ce radar.',
    errorEmail: 'Entre une adresse courriel valide.',
    errorGeneric: 'Impossible de créer l’alerte pour le moment.',
    proof1: 'Last Call',
    proof2: 'Cadeaux',
    proof3: 'Régions',
    proof4: 'Vols',
    howTitle: 'Une logique simple',
    step1: 'Tu décris ce que tu cherches',
    step2: 'Forfeo surveille les offres et expériences',
    step3: 'Tu reçois une sélection quand c’est pertinent',
    instant: 'Instantané',
    daily: 'Résumé quotidien',
    weekly: 'Résumé hebdomadaire',
    continueExplore: 'Explorer les offres',
  },
  en: {
    badge: 'Forfeo smart alerts',
    heroTitleA: 'Watch what you want to experience,',
    heroTitleB: 'Forfeo notifies you.',
    subtitle: 'Create a personalized alert for Last Call deals, restaurants, hotels, gift ideas, Quebec regions and flight alerts. Forfeo analyzes your intent and notifies you when a relevant opportunity appears.',
    create: 'Create my alert',
    manage: 'Manage my alerts',
    formTitle: 'Create a smart alert',
    formSubtitle: 'Choose your intent, budget and region. You only receive alerts that match your criteria.',
    email: 'Email address',
    name: 'First name or name',
    type: 'Alert type',
    flightOrigin: 'Departure airport',
    flightDestination: 'Destination airport',
    travelPeriod: 'Flights period',
    passengers: 'Flightslers',
    directOnly: 'Direct flights only',
    flightBlockTitle: 'Create my flight alert',
    flightBlockText: 'Enter your departure airport, destination and budget. Forfeo sends a confirmation email and watches flight opportunities.',
    flightOriginPlaceholder: 'Ex: YQB, YUL, Quebec City, Montreal',
    flightDestinationPlaceholder: 'Ex: Paris, MIA, YVR, Fort Lauderdale',
    flightPeriodPlaceholder: 'Ex: March 2026, spring break, flexible',
    airportSuggestions: 'Popular suggestions',
    originSuggestions: 'Common departures',
    destinationSuggestions: 'Popular destinations',
    prefilledFlightLink: 'Open prefilled link',
    googleFlightsHint: 'The button opens Google Flights with your departure, destination and travel period already included in the search.',
    errorFlightRoute: 'Enter at least a departure and destination to create a flight alert.',
    region: 'Region',
    city: 'City or neighborhood',
    category: 'Category',
    budget: 'Maximum budget',
    occasion: 'Occasion',
    keywords: 'Keywords / intent',
    frequency: 'Frequency',
    consent: 'I agree to receive Forfeo email alerts related to my criteria. I can manage or disable them at any time.',
    submit: 'Activate my alert',
    sending: 'Activating...',
    successTitle: 'Alert activated',
    successText: 'Your Forfeo radar is ready. You will receive an email when offers or experiences match your criteria.',
    errorConsent: 'You must agree to email alerts to activate this radar.',
    errorEmail: 'Enter a valid email address.',
    errorGeneric: 'Unable to create the alert right now.',
    proof1: 'Last Call',
    proof2: 'Gifts',
    proof3: 'Regions',
    proof4: 'Flights',
    howTitle: 'A simple logic',
    step1: 'You describe what you want',
    step2: 'Forfeo watches offers and experiences',
    step3: 'You receive a selection when it matters',
    instant: 'Instant',
    daily: 'Daily digest',
    weekly: 'Weekly digest',
    continueExplore: 'Explore offers',
  },
};

const alertTypes: Array<{ id: SmartAlertType; fr: string; en: string; icon: React.ReactNode; hintFr: string; hintEn: string; featured?: boolean }> = [
  { id: 'last_call', fr: 'Offres Last Call', en: 'Last Call deals', icon: <FaBolt />, hintFr: 'Prix qui baissent, inventaire limité', hintEn: 'Price drops, limited inventory' },
  { id: 'restaurant', fr: 'Restaurants', en: 'Restaurants', icon: <FaUtensils />, hintFr: 'Soupers, brunchs, sorties romantiques', hintEn: 'Dinner, brunch, romantic outings' },
  { id: 'hotel', fr: 'Hôtels & séjours', en: 'Hotels & stays', icon: <FaHotel />, hintFr: 'Nuitées, escapades, forfaits', hintEn: 'Stays, getaways, packages' },
  { id: 'gift', fr: 'Idées cadeaux', en: 'Gift ideas', icon: <FaGift />, hintFr: 'Anniversaire, fête, merci, entreprise', hintEn: 'Birthday, celebration, thank-you, corporate' },
  { id: 'region', fr: 'Régions du Québec', en: 'Quebec regions', icon: <FaMapMarkedAlt />, hintFr: 'Découvrir une ville ou une région', hintEn: 'Discover a city or region' },
  { id: 'family', fr: 'Famille', en: 'Family', icon: <FaUsers />, hintFr: 'Activités avec enfants, week-ends', hintEn: 'Kid-friendly activities and weekends' },
  { id: 'flight', fr: 'Billets d’avion / vols', en: 'Flights / airfare', icon: <FaPlane />, hintFr: 'Aéroport de départ, destination, budget à surveiller', hintEn: 'Departure airport, destination, budget to watch', featured: true },
  { id: 'corporate', fr: 'Cadeaux corporatifs', en: 'Corporate gifts', icon: <FaStar />, hintFr: 'Employés, clients, équipes', hintEn: 'Employees, clients, teams' },
];

const regions = ['Québec', 'Montréal', 'Charlevoix', 'Mauricie', 'Estrie', 'Laval', 'Outaouais', 'Saguenay', 'Gaspésie', 'Montérégie', 'Laurentides', 'Lanaudière', 'Tout le Québec'];
const categories = ['Toutes', 'Gastronomie', 'Hébergement', 'Spa & détente', 'Aventure', 'Famille', 'Culture', 'Romantique', 'Corporatif'];
const occasions = ['Aucune', 'Anniversaire', 'Saint-Valentin', 'Fête des Mères', 'Fête des Pères', 'Temps des fêtes', 'Graduation', 'Merci', 'Récompense employé', 'Week-end surprise'];

const canadianDepartureAirports = [
  { code: 'YQB', city: 'Québec', name: 'Aéroport international Jean-Lesage de Québec' },
  { code: 'YUL', city: 'Montréal', name: 'Montréal-Trudeau' },
  { code: 'YOW', city: 'Ottawa', name: 'Ottawa International' },
  { code: 'YYZ', city: 'Toronto', name: 'Toronto Pearson' },
  { code: 'YTZ', city: 'Toronto', name: 'Billy Bishop Toronto City' },
  { code: 'YVR', city: 'Vancouver', name: 'Vancouver International' },
  { code: 'YYC', city: 'Calgary', name: 'Calgary International' },
  { code: 'YEG', city: 'Edmonton', name: 'Edmonton International' },
  { code: 'YHZ', city: 'Halifax', name: 'Halifax Stanfield' },
  { code: 'YWG', city: 'Winnipeg', name: 'Winnipeg Richardson' },
];

const popularDestinationAirports = [
  { code: 'CDG', city: 'Paris', name: 'Paris Charles-de-Gaulle' },
  { code: 'ORY', city: 'Paris', name: 'Paris Orly' },
  { code: 'MIA', city: 'Miami', name: 'Miami International' },
  { code: 'FLL', city: 'Fort Lauderdale', name: 'Fort Lauderdale-Hollywood' },
  { code: 'MCO', city: 'Orlando', name: 'Orlando International' },
  { code: 'CUN', city: 'Cancún', name: 'Cancún International' },
  { code: 'PUJ', city: 'Punta Cana', name: 'Punta Cana International' },
  { code: 'LIS', city: 'Lisbonne', name: 'Lisbon Humberto Delgado' },
  { code: 'LHR', city: 'Londres', name: 'London Heathrow' },
  { code: 'JFK', city: 'New York', name: 'John F. Kennedy International' },
  { code: 'EWR', city: 'Newark / New York', name: 'Newark Liberty' },
  { code: 'YVR', city: 'Vancouver', name: 'Vancouver International' },
  { code: 'YYC', city: 'Calgary', name: 'Calgary International' },
];

const airportOptionLabel = (airport: { code: string; city: string; name: string }) => `${airport.code} — ${airport.city} (${airport.name})`;
const airportValue = (airport: { code: string; city: string }) => `${airport.code} — ${airport.city}`;

const buildFlightPartnerUrl = (origin?: string, destination?: string, travelPeriod?: string, passengers?: string | number, directOnly?: boolean) =>
  buildKiwiFlightSearchUrl({
    origin,
    destination,
    travelPeriod,
    passengers,
    directOnly,
    source: 'smart_alerts_preview',
  });

const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const normalizeBudgetInput = (value: unknown) => {
  const raw = String(value ?? '').replace(/[^0-9.,]/g, '').replace(',', '.').trim();
  if (!raw) return '';
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? String(Math.round(n * 100) / 100) : '';
};

export default function SmartAlerts() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const text = copy[lang];
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<SmartAlertPayload>({
    email: '',
    name: '',
    alert_type: 'last_call',
    region: 'Québec',
    city: '',
    category: 'Toutes',
    max_budget: '',
    occasion: 'Aucune',
    keywords: '',
    frequency: 'instant',
    language: lang,
    consent: true,
    origin: '',
    destination: '',
    travel_period: '',
    passengers: '1',
    direct_only: false,
  });

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      const email = data?.session?.user?.email || '';
      if (mounted && email) setForm((prev) => ({ ...prev, email }));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setForm((prev) => ({ ...prev, language: lang }));
  }, [lang]);

  const selectedType = useMemo(() => alertTypes.find((item) => item.id === form.alert_type) || alertTypes[0], [form.alert_type]);
  const isFlightAlert = form.alert_type === 'flight';
  const flightPreviewUrl = useMemo(() => buildFlightPartnerUrl(form.origin, form.destination, form.travel_period, form.passengers, form.direct_only), [form.origin, form.destination, form.travel_period, form.passengers, form.direct_only]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!isEmail(form.email)) {
      setError(text.errorEmail);
      return;
    }
    if (!form.consent) {
      setError(text.errorConsent);
      return;
    }
    if (isFlightAlert && (!String(form.origin || '').trim() || !String(form.destination || '').trim())) {
      setError(text.errorFlightRoute);
      return;
    }

    setLoading(true);
    const normalizedBudget = normalizeBudgetInput(form.max_budget);
    const payloadWithBudget = {
      ...form,
      max_budget: normalizedBudget,
      page_url: window.location.href,
      language: lang,
    };
    const response = await createSmartAlert(payloadWithBudget);

    if (!response.ok) {
      setLoading(false);
      setError(response.details || response.error || text.errorGeneric);
      return;
    }

    setLoading(false);
    setSuccess(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-cyan-200 selection:text-slate-950">
      <Header />
      <main className="pt-24 overflow-hidden">
        <section className="relative px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-cyan-500/20 blur-[140px]" />
            <div className="absolute top-40 right-0 h-[420px] w-[420px] rounded-full bg-fuchsia-500/10 blur-[120px]" />
            <div className="absolute bottom-0 left-0 h-[360px] w-[360px] rounded-full bg-blue-500/10 blur-[100px]" />
          </div>

          <div className="relative mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-cyan-100 backdrop-blur">
                  <FaBell className="text-cyan-300" /> {text.badge}
                </div>
                <h1 className="mt-7 text-4xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95]">
                  {text.heroTitleA}{' '}
                  <span className="block bg-gradient-to-r from-cyan-300 via-blue-300 to-fuchsia-300 bg-clip-text text-transparent">
                    {text.heroTitleB}
                  </span>
                </h1>
                <p className="mt-7 max-w-3xl text-lg md:text-xl leading-8 text-slate-300">
                  {text.subtitle}
                </p>
                <div className="mt-9 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => document.getElementById('smart-alert-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-7 py-4 text-sm font-black uppercase tracking-widest text-slate-950 transition hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-cyan-500/20"
                  >
                    {text.create} <FaChevronRight />
                  </button>
                  <Link
                    to="/mes-alertes-intelligentes"
                    className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-7 py-4 text-sm font-black uppercase tracking-widest text-white backdrop-blur transition hover:bg-white/15"
                  >
                    {text.manage}
                  </Link>
                </div>

                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[text.proof1, text.proof2, text.proof3, text.proof4].map((item, index) => (
                    <div key={item} className="rounded-3xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur">
                      <div className="text-2xl font-black text-white">0{index + 1}</div>
                      <div className="mt-1 text-xs font-black uppercase tracking-widest text-slate-400">{item}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-[2.5rem] border border-white/10 bg-white/[0.08] p-5 shadow-2xl shadow-cyan-950/30 backdrop-blur-2xl">
                  <div className="absolute -inset-px rounded-[2.5rem] bg-gradient-to-br from-cyan-400/20 via-transparent to-fuchsia-400/20 pointer-events-none" />
                  <div className="relative rounded-[2rem] bg-slate-950/80 p-6 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-black uppercase tracking-[0.25em] text-cyan-300">Forfeo Radar</div>
                        <div className="mt-2 text-2xl font-black">{lang === 'en' ? selectedType.en : selectedType.fr}</div>
                      </div>
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-400/10 text-cyan-200 text-2xl">
                        {selectedType.icon}
                      </div>
                    </div>
                    <div className="mt-6 space-y-3">
                      {[text.step1, text.step2, text.step3].map((step, index) => (
                        <div key={step} className="flex items-center gap-3 rounded-2xl bg-white/[0.06] p-4 border border-white/10">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-slate-950 text-sm font-black">{index + 1}</div>
                          <div className="text-sm font-bold text-slate-200">{step}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 rounded-3xl bg-gradient-to-br from-cyan-500 to-blue-600 p-5 text-white">
                      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-cyan-100"><FaShieldAlt /> {text.howTitle}</div>
                      <p className="mt-3 text-sm leading-6 text-white/90">{lang === 'en' ? selectedType.hintEn : selectedType.hintFr}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="smart-alert-form" className="relative bg-white text-slate-950 px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="mx-auto max-w-6xl">
            {success ? (
              <div className="mx-auto max-w-3xl rounded-[2.5rem] border border-emerald-100 bg-emerald-50 p-8 md:p-12 text-center shadow-2xl shadow-emerald-900/10">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-4xl"><FaCheckCircle /></div>
                <h2 className="mt-6 text-3xl md:text-5xl font-black tracking-tight text-slate-950">{text.successTitle}</h2>
                <p className="mt-4 text-lg leading-8 text-slate-600">{text.successText}</p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/mes-alertes-intelligentes" className="inline-flex items-center justify-center gap-3 rounded-2xl bg-slate-950 px-7 py-4 text-sm font-black uppercase tracking-widest text-white">
                    {text.manage}
                  </Link>
                  <Link to="/last-call" className="inline-flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-black uppercase tracking-widest text-slate-800">
                    {text.continueExplore}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-5">
                  <div className="sticky top-28">
                    <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-4 py-2 text-xs font-black uppercase tracking-widest text-cyan-700"><FaSearchLocation /> {text.badge}</div>
                    <h2 className="mt-5 text-3xl md:text-5xl font-black tracking-tight">{text.formTitle}</h2>
                    <p className="mt-4 text-lg leading-8 text-slate-600">{text.formSubtitle}</p>
                    <div className="mt-8 grid gap-3">
                      {alertTypes.map((type) => (
                        <button
                          type="button"
                          key={type.id}
                          onClick={() => setForm((prev) => ({ ...prev, alert_type: type.id }))}
                          className={`relative flex items-center gap-4 rounded-3xl border p-4 text-left transition ${
                            form.alert_type === type.id
                              ? 'border-cyan-300 bg-cyan-50 shadow-lg shadow-cyan-900/5'
                              : type.featured
                                ? 'border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md shadow-blue-900/5 animate-pulse'
                                : 'border-slate-100 bg-white hover:bg-slate-50'
                          }`}
                        >
                          {type.featured && (
                            <span className="absolute -right-2 -top-2 rounded-full bg-blue-600 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-white shadow-lg">
                              {lang === 'en' ? 'Flights' : 'Vols'}
                            </span>
                          )}
                          <div className={`flex h-12 w-12 items-center justify-center rounded-2xl text-xl ${form.alert_type === type.id ? 'bg-cyan-600 text-white' : type.featured ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{type.icon}</div>
                          <div>
                            <div className="font-black text-slate-950">{lang === 'en' ? type.en : type.fr}</div>
                            <div className="text-sm text-slate-500">{lang === 'en' ? type.hintEn : type.hintFr}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <form onSubmit={handleSubmit} className="rounded-[2.5rem] border border-slate-100 bg-slate-50 p-5 md:p-8 shadow-2xl shadow-slate-900/10">
                    <div className="grid md:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.email}</span>
                        <div className="mt-2 relative"><FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" /><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-2xl border border-slate-200 bg-white py-4 pl-11 pr-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100" /></div>
                      </label>
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.name}</span>
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100" />
                      </label>
                    </div>

                    <div className="mt-4 grid md:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.type}</span>
                        <select value={form.alert_type} onChange={(e) => setForm({ ...form, alert_type: e.target.value as SmartAlertType })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100">
                          {alertTypes.map((type) => <option key={type.id} value={type.id}>{lang === 'en' ? type.en : type.fr}</option>)}
                        </select>
                      </label>
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.region}</span>
                        <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100">
                          {regions.map((region) => <option key={region}>{region}</option>)}
                        </select>
                      </label>
                    </div>
                    {isFlightAlert && (
                      <div className="mt-5 rounded-[2rem] border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-5 shadow-lg shadow-blue-900/5">
                        <div className="mb-4 flex items-start gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-xl text-white"><FaPlane /></div>
                          <div>
                            <h3 className="text-xl font-black text-slate-950">{text.flightBlockTitle}</h3>
                            <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">{text.flightBlockText}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <label className="block">
                            <span className="ml-1 text-xs font-black uppercase tracking-widest text-blue-700">{text.flightOrigin}</span>
                            <input list="forfeo-origin-airports" value={form.origin || ''} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder={text.flightOriginPlaceholder} className="mt-2 w-full rounded-2xl border border-blue-100 bg-white px-4 py-4 font-bold outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" />
                            <datalist id="forfeo-origin-airports">
                              {canadianDepartureAirports.map((airport) => <option key={airport.code} value={airportValue(airport)}>{airportOptionLabel(airport)}</option>)}
                            </datalist>
                          </label>
                          <label className="block">
                            <span className="ml-1 text-xs font-black uppercase tracking-widest text-blue-700">{text.flightDestination}</span>
                            <input list="forfeo-destination-airports" value={form.destination || ''} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder={text.flightDestinationPlaceholder} className="mt-2 w-full rounded-2xl border border-blue-100 bg-white px-4 py-4 font-bold outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" />
                            <datalist id="forfeo-destination-airports">
                              {popularDestinationAirports.map((airport) => <option key={airport.code} value={airportValue(airport)}>{airportOptionLabel(airport)}</option>)}
                            </datalist>
                          </label>
                        </div>
                        <div className="mt-4 grid gap-4 lg:grid-cols-2">
                          <div className="rounded-2xl border border-blue-100 bg-white/70 p-4">
                            <div className="text-[10px] font-black uppercase tracking-widest text-blue-700">{text.originSuggestions}</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {canadianDepartureAirports.slice(0, 6).map((airport) => (
                                <button type="button" key={airport.code} onClick={() => setForm({ ...form, origin: airportValue(airport) })} className="rounded-full border border-blue-100 bg-white px-3 py-2 text-xs font-black text-slate-700 transition hover:border-blue-400 hover:bg-blue-50">
                                  {airport.code} · {airport.city}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="rounded-2xl border border-blue-100 bg-white/70 p-4">
                            <div className="text-[10px] font-black uppercase tracking-widest text-blue-700">{text.destinationSuggestions}</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {popularDestinationAirports.slice(0, 8).map((airport) => (
                                <button type="button" key={airport.code} onClick={() => setForm({ ...form, destination: airportValue(airport) })} className="rounded-full border border-blue-100 bg-white px-3 py-2 text-xs font-black text-slate-700 transition hover:border-blue-400 hover:bg-blue-50">
                                  {airport.code} · {airport.city}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 grid md:grid-cols-3 gap-4">
                          <label className="block md:col-span-2">
                            <span className="ml-1 text-xs font-black uppercase tracking-widest text-blue-700">{text.travelPeriod}</span>
                            <input value={form.travel_period || ''} onChange={(e) => setForm({ ...form, travel_period: e.target.value })} placeholder={text.flightPeriodPlaceholder} className="mt-2 w-full rounded-2xl border border-blue-100 bg-white px-4 py-4 font-bold outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" />
                          </label>
                          <label className="block">
                            <span className="ml-1 text-xs font-black uppercase tracking-widest text-blue-700">{text.passengers}</span>
                            <input type="number" min="1" value={form.passengers || '1'} onChange={(e) => setForm({ ...form, passengers: e.target.value })} className="mt-2 w-full rounded-2xl border border-blue-100 bg-white px-4 py-4 font-bold outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" />
                          </label>
                        </div>
                        <label className="mt-4 flex items-center gap-3 rounded-2xl border border-blue-100 bg-white p-4 cursor-pointer">
                          <input type="checkbox" checked={Boolean(form.direct_only)} onChange={(e) => setForm({ ...form, direct_only: e.target.checked })} className="h-5 w-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500" />
                          <span className="text-sm font-black text-slate-800">{text.directOnly}</span>
                        </label>
                        <div className="mt-4 rounded-2xl border border-blue-100 bg-white p-4">
                          <div className="text-sm font-bold leading-6 text-slate-600">{text.googleFlightsHint}</div>
                          <a href={flightPreviewUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-xs font-black uppercase tracking-widest text-white transition hover:bg-blue-700">
                            <FaPlane /> {text.prefilledFlightLink}
                          </a>
                        </div>
                      </div>
                    )}


                    <div className="mt-4 grid md:grid-cols-2 gap-4">
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.city}</span>
                        <div className="mt-2 relative"><FaCity className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" /><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Québec, Montréal, Charlevoix..." className="w-full rounded-2xl border border-slate-200 bg-white py-4 pl-11 pr-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100" /></div>
                      </label>
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.category}</span>
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100">
                          {categories.map((category) => <option key={category}>{category}</option>)}
                        </select>
                      </label>
                    </div>

                    <div className="mt-4 grid md:grid-cols-3 gap-4">
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.budget}</span>
                        <input type="number" min="0" value={form.max_budget} onChange={(e) => setForm({ ...form, max_budget: e.target.value })} placeholder="150" className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100" />
                      </label>
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.occasion}</span>
                        <select value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100">
                          {occasions.map((occasion) => <option key={occasion}>{occasion}</option>)}
                        </select>
                      </label>
                      <label className="block">
                        <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.frequency}</span>
                        <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value as any })} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100">
                          <option value="instant">{text.instant}</option>
                          <option value="daily">{text.daily}</option>
                          <option value="weekly">{text.weekly}</option>
                        </select>
                      </label>
                    </div>

                    <label className="mt-4 block">
                      <span className="ml-1 text-xs font-black uppercase tracking-widest text-slate-500">{text.keywords}</span>
                      <textarea value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} rows={4} placeholder="Ex: spa romantique, activité famille, hôtel vieux Québec, brunch fête des mères..." className="mt-2 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100" />
                    </label>

                    <label className="mt-5 flex items-start gap-3 rounded-3xl border border-cyan-100 bg-cyan-50 p-4 cursor-pointer">
                      <input type="checkbox" checked={Boolean(form.consent)} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 h-5 w-5 rounded border-cyan-300 text-cyan-600 focus:ring-cyan-500" />
                      <span className="text-sm font-bold leading-6 text-cyan-950">{text.consent}</span>
                    </label>

                    {error && <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}

                    <button disabled={loading} className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-cyan-600 to-blue-700 px-7 py-5 text-sm font-black uppercase tracking-widest text-white shadow-xl shadow-cyan-900/20 transition hover:-translate-y-0.5 disabled:opacity-60">
                      {loading ? <FaRegClock className="animate-spin" /> : <FaPaperPlane />} {loading ? text.sending : text.submit}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
